import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { signInWithPopup, signOut, onAuthStateChanged, GoogleAuthProvider } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';

const GOOGLE_ICON = 'https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg';
const CRM_DEMO_URL = process.env.REACT_APP_CRM_DEMO_URL || '';

export default function Demo() {
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [googleIdToken, setGoogleIdToken] = useState(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const sendTokenToIframe = useCallback(() => {
    if (iframeRef.current && googleIdToken && iframeLoaded) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'DEMO_AUTH', googleIdToken },
        CRM_DEMO_URL
      );
    }
  }, [googleIdToken, iframeLoaded]);

  useEffect(() => {
    sendTokenToIframe();
  }, [sendTokenToIframe]);

  useEffect(() => {
    const handler = (e) => {
      if (e.origin === CRM_DEMO_URL && e.data?.type === 'DEMO_READY') {
        sendTokenToIframe();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sendTokenToIframe]);

  const handleLogin = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.idToken) {
        setGoogleIdToken(credential.idToken);
      }
    } catch (err) {
      if (err.code !== 'auth/popup-closed-by-user') {
        console.error('Login error:', err);
      }
    }
  };

  const handleLogout = async () => {
    if (iframeRef.current && iframeLoaded) {
      iframeRef.current.contentWindow.postMessage(
        { type: 'DEMO_LOGOUT' },
        CRM_DEMO_URL
      );
    }
    await signOut(auth);
    setGoogleIdToken(null);
    setIframeLoaded(false);
  };

  if (loading) {
    return (
      <div className="demo-login">
        <div className="demo-loading">
          <div className="demo-spinner" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="demo-login">
        <div className="demo-login-card">
          <img src="/crm-logo.png" alt="CRM Cloud" style={{ width: 80, height: 80, marginBottom: 16 }} />
          <h1>CRM-<span>Pro</span></h1>
          <p className="demo-login-subtitle">{t('demo.subtitle')}</p>
          <p className="demo-login-prompt">{t('demo.loginPrompt')}</p>
          <button className="btn btn-google" onClick={handleLogin}>
            <img src={GOOGLE_ICON} alt="Google" />
            {t('demo.loginButton')}
          </button>
          <div style={{ marginTop: 24 }}>
            <Link to="/" className="btn btn-secondary" style={{ fontSize: '0.9rem', padding: '8px 20px' }}>
              {t('demo.backToSite')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <div className="demo-header">
        <div className="demo-header-left">
          <Link to="/" className="nav-logo" style={{ fontSize: '1.2rem' }}>
            <img src="/crm-logo.png" alt="CRM Cloud" className="nav-logo-img" style={{ width: 30, height: 30 }} />
            CRM Cloud
          </Link>
          <span className="demo-user-badge">DEMO</span>
        </div>
        <div className="demo-header-right">
          <div className="demo-user-info">
            {user.photoURL && (
              <img src={user.photoURL} alt="" className="demo-user-avatar" referrerPolicy="no-referrer" />
            )}
            <span className="demo-user-name">{user.displayName}</span>
          </div>
          <button className="btn btn-secondary" onClick={handleLogout} style={{ padding: '6px 16px', fontSize: '0.85rem' }}>
            {t('demo.logout')}
          </button>
        </div>
      </div>
      <div className="demo-iframe-wrapper">
        {!iframeLoaded && (
          <div className="demo-loading">
            <div className="demo-spinner" />
            <p style={{ color: 'var(--text-secondary)' }}>{t('demo.loading')}</p>
          </div>
        )}
        {CRM_DEMO_URL ? (
          <iframe
            ref={iframeRef}
            src={CRM_DEMO_URL + '?demo=true'}
            className="demo-iframe"
            title="CRM Cloud Demo"
            onLoad={() => setIframeLoaded(true)}
            allow="clipboard-write"
          />
        ) : (
          <div className="demo-loading" style={{ position: 'relative' }}>
            <div style={{
              textAlign: 'center',
              padding: 40,
              background: 'white',
              borderRadius: 16,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              maxWidth: 500
            }}>
              <h2 style={{ marginBottom: 12, fontSize: '1.5rem' }}>
                {t('demo.welcome')}, {user.displayName?.split(' ')[0]}!
              </h2>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
                {t('demo.exploring')}
              </p>
              <p style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
                Demo URL not configured. Set <code style={{
                  background: '#f1f5f9',
                  padding: '2px 8px',
                  borderRadius: 6,
                  fontSize: '0.85rem'
                }}>REACT_APP_CRM_DEMO_URL</code> in your .env file.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
